/* La Sura - Party Planner. Rules, events and file split: sections/lasura-party-planner.liquid. */
import { londonNow, fromIso, closures, isClosed, earliest } from '@theme/lasura-kitchen-dates';
import { planFilter } from '@theme/lasura-plan-filter';

const KEY = 'lasura:party';
const fill = (s, v) => Object.entries(v).reduce((o, [k, x]) => o.split(`{${k}}`).join(String(x)), s || '');

class LasuraPartyPlanner extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    try {
      this.words = JSON.parse(this.querySelector('[data-plan-words]').textContent);
      this.products = JSON.parse(this.querySelector('[data-plan-products]').textContent);
    } catch {
      return;
    }
    const d = this.dataset;
    this.hour = +d.hour;
    this.shut = closures(d);
    this.big = +d.big || 30;
    const l = document.documentElement.lang || 'en';
    const lang = (this.lang = l == 'en' ? 'en-GB' : l);
    this.format = new Intl.DateTimeFormat(lang, { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' });
    this.formatLong = new Intl.DateTimeFormat(lang, { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' });
    let saved = {};
    try {
      saved = JSON.parse(localStorage.getItem(KEY) || '{}') || {};
    } catch {}
    this.guests = +saved.guests > 0 ? +saved.guests : null;
    this.date = saved.date && fromIso(saved.date) >= this.today ? saved.date : null;
    this.filter = planFilter(this);

    this.addEventListener('click', async (event) => {
      const t = event.target.closest('button');
      if (!t || !this.contains(t)) return;
      this.ui ||= await import(d.ui);
      this.ui.click(this, t);
    });
    this.addEventListener('keydown', (event) => this.ui?.key(this, event));
    this.onOutside = (event) => !this.contains(event.target) && this.ui?.close(this, false);
    document.addEventListener('click', this.onOutside);
    this.onCart = async () => (this.ui ||= await import(d.ui)).sync(this);
    document.addEventListener('cart:update', this.onCart);
    document.addEventListener('click', (e) => {
      const li = e.target.closest?.('.lasura-plan-is-late');
      if (li) this.track('plan_late_click', { product: li.dataset.productId, date: this.date });
    });
    document.addEventListener('filter:update', () =>
      setTimeout(() => document.querySelector('.main-collection-grid__empty') && this.track('collection_empty', { filters: location.search }), 800));

    this.hidden = false;
    this.renderTokens();
    const host = document.querySelector('.product-grid')?.closest('.shopify-section') || document.body;
    this.observer = new MutationObserver(() => {
      if (this.moving) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.apply(), 60);
    });
    this.observer.observe(host, { childList: true, subtree: true });
    this.apply();
    if (matchMedia('(max-width: 999px)').matches) import(d.bar).then((m) => m.bar(this));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onOutside);
    document.removeEventListener('cart:update', this.onCart);
    this.observer?.disconnect();
  }

  /* Read fresh: a tab left open past midnight moves on. */
  get today() {
    return londonNow().today;
  }

  earliest(days) {
    return earliest(days, this.hour, this.shut);
  }

  isClosed(t) {
    return isClosed(t, this.shut);
  }

  plannable() {
    return Object.values(this.products).filter((p) => !p.post && p.days >= 0);
  }

  readyCount(t) {
    return this.plannable().filter((p) => this.earliest(p.days) <= t).length;
  }

  renderTokens(fresh) {
    const w = this.words;
    this.querySelector('[data-plan-guests-text]').textContent = this.guests ? fill(w.guests, { n: this.guests }) : w.guestsEmpty;
    this.querySelector('[data-plan-date-text]').textContent = this.date ? this.format.format(fromIso(this.date)) : w.dateEmpty;
    this.querySelector('[data-plan-stop]').hidden = !this.date;
    this.querySelector('[data-plan-count]').textContent = String(this.guests || 12);
    for (const c of this.querySelectorAll('[data-plan-guests]')) c.setAttribute('aria-pressed', String(+c.dataset.planGuests === this.guests));
    const token = fresh && this.querySelector(`[data-plan-open="${fresh}"]`);
    if (token) {
      token.classList.remove('is-new');
      void token.offsetWidth;
      token.classList.add('is-new');
    }
  }

  save(fresh) {
    try {
      localStorage.setItem(KEY, JSON.stringify({ guests: this.guests, date: this.date }));
    } catch {}
    this.renderTokens(fresh);
    this.apply();
    this.ui?.sync(this);
    this.dispatchEvent(new Event('lasura:party'));
    this.track('plan_set', { guests: this.guests, date: this.date, ready: this.date ? this.readyCount(fromIso(this.date)) : null, total: this.plannable().length });
    if (fresh == 'date') this.filter?.go(this.date);
  }

  el(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text) e.textContent = text;
    return e;
  }

  apply() {
    const grid = document.querySelector('.product-grid');
    if (!grid) return;
    /* A party date that has passed while the tab stayed open is no longer a plan. */
    if (this.date && fromIso(this.date) < this.today) {
      this.date = null;
      this.renderTokens();
    }
    this.moving = true;
    const y = this.getBoundingClientRect().top;
    const w = this.words;
    const items = [...grid.querySelectorAll(':scope > .product-grid__item')];
    items.forEach((li, i) => (li.dataset.planIndex ??= String((+li.dataset.page || 1) * 1000 + i)));
    const motion = document.body.classList.contains('lasura-motion') && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    const before = motion ? new Map(items.map((li) => [li, li.getBoundingClientRect()])) : null;

    grid.querySelectorAll(':scope > .lasura-plan-divider, :scope > .lasura-plan-big, .lasura-plan-pill').forEach((e) => e.remove());
    items.forEach((li) => li.classList.remove('lasura-plan-is-late'));
    const byIndex = (a, b) => a.dataset.planIndex - b.dataset.planIndex;
    let order = [...items].sort(byIndex);
    let ready = 0;

    if (this.date) {
      const t = fromIso(this.date);
      const short = this.format.format(t);
      const on = [];
      const late = [];
      const other = [];
      for (const li of order) {
        const p = this.products[li.dataset.productId];
        if (!p || p.post || p.days < 0) other.push(li);
        else if (this.earliest(p.days) <= t) {
          on.push(li);
          this.pill(li, fill(w.ready, { date: short }), false);
        } else {
          const e = this.earliest(p.days);
          li.dataset.planEarliest = String(e);
          li.classList.add('lasura-plan-is-late');
          late.push(li);
          this.pill(li, fill(w.late, { date: this.format.format(e) }), true);
        }
      }
      late.sort((a, b) => a.dataset.planEarliest - b.dataset.planEarliest || byIndex(a, b));
      ready = on.length;
      order = [...on, ...other];
      if (late.length) {
        const div = this.el('li', 'lasura-plan-divider');
        div.append(this.el('h2', 'h3', fill(w.divider, { date: short })));
        order.push(div, ...late);
      }
    }

    /* Feature cards (snippets/lasura-grid-feature) go back after the same number of cakes. */
    for (const f of grid.querySelectorAll(':scope > .lasura-grid-feature')) {
      let c = 0, i = 0;
      while (i < order.length && c < +f.dataset.after) if (order[i++].dataset.productId) c++;
      order.splice(i, 0, f);
    }

    if (this.guests && this.guests >= this.big) {
      const big = this.el('li', 'lasura-plan-big');
      big.append(this.el('h2', 'h3', fill(w.bigHeading, { n: this.big })), this.el('p', '', w.bigText));
      if (w.bigUrl && w.bigLabel) {
        const a = this.el('a', 'button', w.bigLabel);
        a.href = w.bigUrl;
        big.append(a);
      }
      order.unshift(big);
    }

    const current = [...grid.children].filter((e) => e.matches('.product-grid__item, .lasura-grid-feature, .lasura-plan-divider, .lasura-plan-big'));
    if (current.length !== order.length || current.some((e, i) => e !== order[i])) {
      const frag = document.createDocumentFragment();
      order.forEach((e) => frag.append(e));
      grid.append(frag);
    }

    for (const li of before ? items : []) {
      const b = before.get(li);
      const a = li.getBoundingClientRect();
      const dx = b.left - a.left;
      const dy = b.top - a.top;
      if (Math.abs(dx) + Math.abs(dy) < 1 || a.bottom < 0 || a.top > innerHeight * 2) continue;
      li.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 520, easing: 'cubic-bezier(.22,1,.36,1)' });
    }

    const long = this.date && this.formatLong.format(fromIso(this.date));
    this.querySelector('[data-plan-summary]').textContent = !this.date
      ? ''
      : ready
        ? fill(w.summary, { count: this.readyCount(fromIso(this.date)), total: this.plannable().length, date: long })
        : fill(w.none, { date: long });
    this.filter?.after();
    const dy = this.getBoundingClientRect().top - y;
    if (dy) scrollBy({ top: dy, behavior: 'instant' });
    queueMicrotask(() => (this.moving = false));
  }

  track(name, detail) {
    document.dispatchEvent(new CustomEvent(`lasura:${name}`, { detail }));
    try {
      Shopify.analytics.publish(name, detail);
    } catch {}
  }

  pill(li, text, late) {
    const content = li.querySelector('.product-card__content');
    if (!content) return;
    const pill = this.el('span', `lasura-plan-pill${late ? ' lasura-plan-pill--late' : ''}`, text);
    const before = content.querySelector('.lasura-card-details, .lasura-card-ful');
    before ? before.before(pill) : content.append(pill);
  }

}

if (!customElements.get('lasura-party-planner')) customElements.define('lasura-party-planner', LasuraPartyPlanner);
